import { useEffect, useRef, useCallback } from 'preact/hooks'
import { Loader2, AlertCircle } from 'lucide-preact'
import { touchPointsApi } from '@/api/touchPoints'
import { websApi } from '@/api/webs'
import { useToast } from '@/hooks/useToast'
import { useTheme } from '@/hooks/useTheme'
import { useUserProfile } from '@/hooks/useUserProfile'
import { CalendarHeader } from './CalendarHeader'
import { CalendarTheme } from './CalendarTheme'
import { useCalendarEvents } from './hooks/useCalendarEvents'
import { useCalendarInstance } from './hooks/useCalendarInstance'
import { useTimeZones } from './hooks/useTimeZones'
import { convertEventsToScheduleX } from './utils/eventConverter'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import '@schedule-x/theme-default/dist/index.css'

interface ScheduleXEvent {
  id: string
  title: string
  start: unknown
  end?: unknown
  calendarId?: string
  _etag?: string
  _updating?: boolean
  _savedStart?: unknown
  _savedEnd?: unknown
  _savedTitle?: string
  [key: string]: unknown
}

interface CalendarApp {
  events: {
    set: (events: ScheduleXEvent[]) => void
    getAll: () => ScheduleXEvent[]
    update: (event: ScheduleXEvent) => void
  }
  render: (element: HTMLElement) => void
}

interface ScheduleXCalendarProps {
  clientCompanyId: string
  workspaceId: string
  campaignId?: string
  webId?: string
}

export function ScheduleXCalendar({ clientCompanyId, workspaceId, campaignId, webId }: ScheduleXCalendarProps) {
  const { showSuccess, showError, showErrorUnless422 } = useToast()
  const { resolvedTheme } = useTheme()
  const { getWorkspaceCapabilities } = useUserProfile()
  const { events, loading, error, refetch } = useCalendarEvents(workspaceId, campaignId, webId)
  const { getDisplayName } = useTimeZones()
  const updateInProgressRef = useRef<Set<string>>(new Set())
  const calendarAppRef = useRef<CalendarApp | null>(null)
  const { updateContext } = useAIChatContextUpdater()

  // Get workspace capabilities - only users with edit permission can modify events
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)

  // Update AI context based on calendar level (workspace, campaign, or web)
  useEffect(() => {
    // Determine location based on which IDs are provided
    let location: typeof ChatLocations[keyof typeof ChatLocations]
    let entityType: typeof ChatEntityTypes[keyof typeof ChatEntityTypes] | undefined
    let entityId: string | undefined

    if (campaignId) {
      location = ChatLocations.CampaignCalendar
      entityType = ChatEntityTypes.Campaign
      entityId = campaignId
    } else if (webId) {
      location = ChatLocations.WebCalendar
      entityType = ChatEntityTypes.Web
      entityId = webId
    } else {
      location = ChatLocations.WorkspaceCalendar
      entityType = undefined
      entityId = undefined
    }

    updateContext({
      campaignId,
      webId,
      location,
      entityType,
      entityId,
      viewMode: ChatViewModes.View,
      availableFields: [
        { name: 'eventCount', displayName: 'Event Count', fieldType: 'number', currentValue: events.length.toString(), isEditable: false, isFocused: false },
        { name: 'calendarLevel', displayName: 'Calendar Level', fieldType: 'text', currentValue: campaignId ? 'Campaign' : webId ? 'Web' : 'Workspace', isEditable: false, isFocused: false },
      ],
      availableActions: [
        'View Event Details',
        'Edit Event',
        'Refresh Calendar',
      ],
    })
  }, [workspaceId, campaignId, webId, events.length, updateContext])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEventUpdate = useCallback(async (updatedEvent: any) => {
    const originalEvent = events.find(e => e.Id === String(updatedEvent.id))
    if (!originalEvent || !calendarAppRef.current) {
      return
    }

    // Get the current calendar event
    const calendarEvent = calendarAppRef.current.events.getAll().find((e: ScheduleXEvent) => e.id === originalEvent.Id)
    if (!calendarEvent) {
      return
    }

    // Email touchpoints don't have a single ScheduledDate — send timing is
    // controlled by EmailSendRule, which calendar drag can't express. The
    // event converter sets disableDND/disableResize, so this is defensive:
    // bail before any state mutation so a stale drag can't leave the event
    // stuck in a "Saving..." state.
    if (originalEvent.Type === 'Email') {
      return
    }

    // Check if event is already being updated
    if (calendarEvent._updating) {
      // Revert to saved state
      calendarAppRef.current.events.update({
        ...calendarEvent,
        start: calendarEvent._savedStart,
        end: calendarEvent._savedEnd,
        title: calendarEvent._savedTitle || calendarEvent.title,
      })
      return
    }

    // Prevent concurrent updates on the same event
    if (updateInProgressRef.current.has(originalEvent.Id)) {
      return
    }

    // Mark event as updating IMMEDIATELY and save current state
    const lockedTitle = `${calendarEvent.title} (Saving...)`

    // Use .update() to properly persist the lock state
    calendarAppRef.current.events.update({
      ...calendarEvent,
      _updating: true,
      _savedStart: updatedEvent.start,
      _savedEnd: updatedEvent.end,
      _savedTitle: calendarEvent.title,
      title: lockedTitle,
    })

    updateInProgressRef.current.add(originalEvent.Id)

    try {
      // Convert Temporal objects to ISO strings for API
      let startDate: Date
      let endDate: Date | undefined

      // Check if it's already a Temporal.ZonedDateTime
      if (updatedEvent.start.epochMilliseconds !== undefined) {
        // It's a ZonedDateTime - use epochMilliseconds
        startDate = new Date(updatedEvent.start.epochMilliseconds)
        endDate = updatedEvent.end ? new Date(updatedEvent.end.epochMilliseconds) : undefined
      } else if (updatedEvent.start.year !== undefined) {
        // It's a PlainDate (all-day event) - convert to midnight in user's local timezone
        const viewerTimeZone = globalThis.Temporal.Now.timeZoneId()
        const plainDate = globalThis.Temporal.PlainDate.from({
          year: updatedEvent.start.year,
          month: updatedEvent.start.month,
          day: updatedEvent.start.day
        })
        // Create a ZonedDateTime at midnight in the user's timezone
        const zonedDateTime = plainDate.toZonedDateTime({ timeZone: viewerTimeZone, plainTime: '00:00:00' })
        startDate = new Date(zonedDateTime.epochMilliseconds)
      } else {
        // Fallback to string conversion
        startDate = new Date(String(updatedEvent.start))
        endDate = updatedEvent.end ? new Date(String(updatedEvent.end)) : undefined
      }

      // Determine if this is an Online Event, Launch Date, or TouchPoint
      const isOnlineEvent = originalEvent.Type === 'Online Event'
      const isLaunchDate = originalEvent.Type === 'Launch Date'

      if (isOnlineEvent) {
        // Update web schedule (Online Event)
        // For OnDemand mode events, they may be all-day (no EndDate/TimeZone)
        // For Upcoming/Hybrid modes, they have both EndDate and TimeZone
        // Always use the current calendar event's ETag (might have been updated during a held drag)
        const currentETag = calendarEvent._etag || originalEvent.SourceETag || ''
        const dto = {
          StartDateTime: startDate.toISOString(),
          EndDateTime: endDate ? endDate.toISOString() : undefined,
          TimeZone: originalEvent.TimeZone || undefined,
          ETag: currentETag,
        }

        const response = await websApi.patchSchedule(workspaceId, originalEvent.SourceId, dto)

        if (response.error) {
          console.error('Web schedule update failed:', response.error)
          showErrorUnless422('Update Failed', response)
          // Revert to the saved state
          if (calendarEvent._savedStart) {
            calendarAppRef.current.events.update({
              ...calendarEvent,
              start: calendarEvent._savedStart,
              end: calendarEvent._savedEnd,
              title: calendarEvent._savedTitle || originalEvent.Title,
              _updating: false,
            })
          }
        } else {
          // Success - update only the ETag in the calendar event
          const updatedWeb = response.data

          if (updatedWeb && calendarAppRef.current?.events) {
            // Update the calendar event's _etag for next drag and clear updating flag
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const updatedCalendarEvent = calendarAppRef.current.events.getAll().find((e: any) => e.id === originalEvent.Id)
            if (updatedCalendarEvent) {
              // Use .update() to persist the changes
              calendarAppRef.current.events.update({
                ...updatedCalendarEvent,
                _etag: updatedWeb.ETag,
                _updating: false,
                _savedStart: undefined,
                _savedEnd: undefined,
                _savedTitle: undefined,
                title: updatedCalendarEvent._savedTitle || originalEvent.Title,
              })
            }

            // DON'T call setEvents - it triggers useEffect and reconverts all events
            // The calendar already has the correct visual position
          }
          showSuccess('Event Updated', 'The event has been updated.')
        }
      } else if (isLaunchDate) {
        // Update web launch date (Launch Date event - all-day event)
        // Convert PlainDate to YYYY-MM-DD format
        let launchDateStr: string
        if (updatedEvent.start.year !== undefined) {
          // It's a PlainDate
          const year = updatedEvent.start.year
          const month = String(updatedEvent.start.month).padStart(2, '0')
          const day = String(updatedEvent.start.day).padStart(2, '0')
          launchDateStr = `${year}-${month}-${day}`
        } else {
          // Fallback: parse from ISO string
          launchDateStr = String(updatedEvent.start).split('T')[0]
        }

        const currentETag = calendarEvent._etag || originalEvent.SourceETag || ''
        const dto = {
          LaunchDate: launchDateStr,
          ETag: currentETag,
        }

        const response = await websApi.patchLaunchDate(workspaceId, originalEvent.SourceId, dto)

        if (response.error) {
          console.error('Web launch date update failed:', response.error)
          showErrorUnless422('Update Failed', response)
          // Revert to the saved state
          if (calendarEvent._savedStart) {
            calendarAppRef.current.events.update({
              ...calendarEvent,
              start: calendarEvent._savedStart,
              end: calendarEvent._savedEnd,
              title: calendarEvent._savedTitle || originalEvent.Title,
              _updating: false,
            })
          }
        } else {
          // Success - update ETag in the calendar event
          const newETag = response.data?.ETag || originalEvent.SourceETag
          if (calendarAppRef.current?.events) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const updatedCalendarEvent = calendarAppRef.current.events.getAll().find((e: any) => e.id === originalEvent.Id)
            if (updatedCalendarEvent) {
              calendarAppRef.current.events.update({
                ...updatedCalendarEvent,
                _etag: newETag,
                _updating: false,
                _savedStart: undefined,
                _savedEnd: undefined,
                _savedTitle: undefined,
                title: updatedCalendarEvent._savedTitle || originalEvent.Title,
              })
            }
          }
          showSuccess('Launch Date Updated', 'The launch date has been updated.')
        }
      } else {
        // Update touchpoint schedule (Meeting, Phone, Social — Email is bailed at top)
        // Always use the current calendar event's ETag (might have been updated during a held drag)
        const currentETag = calendarEvent._etag || originalEvent.SourceETag || ''
        const dto = {
          StartDate: startDate.toISOString(),
          EndDate: endDate ? endDate.toISOString() : undefined,
          ETag: currentETag,
        }

        const response = await touchPointsApi.patchSchedule(workspaceId, originalEvent.SourceId, dto)

        if (response.error) {
          console.error('Touchpoint schedule update failed:', response.error)
          showErrorUnless422('Update Failed', response)
          // Revert to the saved state
          if (calendarEvent._savedStart) {
            calendarAppRef.current.events.update({
              ...calendarEvent,
              start: calendarEvent._savedStart,
              end: calendarEvent._savedEnd,
              title: calendarEvent._savedTitle || originalEvent.Title,
              _updating: false,
            })
          }
        } else {
          // Success - update ETag in the calendar event
          const newETag = response.data?.ETag || originalEvent.SourceETag
          if (calendarAppRef.current?.events) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const updatedCalendarEvent = calendarAppRef.current.events.getAll().find((e: any) => e.id === originalEvent.Id)
            if (updatedCalendarEvent) {
              // Use .update() to persist the changes
              calendarAppRef.current.events.update({
                ...updatedCalendarEvent,
                _etag: newETag,
                _updating: false,
                _savedStart: undefined,
                _savedEnd: undefined,
                _savedTitle: undefined,
                title: updatedCalendarEvent._savedTitle || originalEvent.Title,
              })
            }
          }
          // DON'T call setEvents - it triggers useEffect and reconverts all events
          // The calendar already has the correct visual position
          showSuccess('Event Updated', 'The event has been moved.')
        }
      }
    } catch {
      showError('Error', 'Failed to update event.')
      // Revert to the saved state
      if (calendarEvent?._savedStart) {
        calendarAppRef.current.events.update({
          ...calendarEvent,
          start: calendarEvent._savedStart,
          end: calendarEvent._savedEnd,
          title: calendarEvent._savedTitle || originalEvent.Title,
          _updating: false,
        })
      }
    } finally {
      // Always clear the update lock
      if (originalEvent) {
        updateInProgressRef.current.delete(originalEvent.Id)
      }
    }
  }, [events, workspaceId, showSuccess, showError])

  const calendarRef = useCalendarInstance({
    loading,
    resolvedTheme,
    onEventUpdate: handleEventUpdate,
    calendarAppRef,
    canEdit, // Pass edit permission to calendar instance
  })

  // Set events on calendar whenever events or calendar app changes
  useEffect(() => {
    if (!calendarAppRef.current) {
      return
    }

    if (events.length === 0) {
      return
    }

    try {
      const scheduleXEvents = convertEventsToScheduleX(events, getDisplayName)
      calendarAppRef.current.events.set(scheduleXEvents)
    } catch (error) {
      console.error('Error converting events:', error)
    }
  }, [events, getDisplayName])

  return (
    <div className="p-6 space-y-6">
      <CalendarTheme />

      <CalendarHeader eventCount={events.length} />

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Failed to load calendar events</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
              <button
                onClick={refetch}
                className="mt-2 text-sm font-medium text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      {!loading && !error && (
        <div
          ref={calendarRef}
          className={`rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden ${resolvedTheme === 'dark' ? 'dark' : ''}`}
          style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}
          data-theme={resolvedTheme}
        />
      )}
    </div>
  )
}
